import React, { useEffect, useState } from 'react';
import NavIcon from './navIcons';
import { SETTINGS_CHANGED } from './settingsEvents';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { connectWebSocket, disconnectWebSocket, sendGlobalAction } from './store/Actions';
import { unsubscribeAccount } from './webpush';
import { wsUrl } from './store/wsUrl';
import { reconnectWs as sharedReconnectWs } from './store/reconnectWs';
import { getStationUrlPath, getPathStationId } from './store/websocketMiddleware';
import useStationSubscription from './stationSubscription';
import LoginModal from './LoginModal';
import { BrandLogo, EVERY_TIME } from './brandLogo';
import useReturnAfterAway from './hooks/useReturnAfterAway';
import useIsMobile from './hooks/useIsMobile';
import useBodyScrollLock from './hooks/useBodyScrollLock';
import './AuthHeader.css';   // save-gallery-link: mobile slide-out enter/exit animation

// The authenticated user's status + nav cluster in the top-right of the header.
// Anonymous: a "Log in" item opens the modal. Logged in: your name is a LINK to
// /profile, with "Log out" beside it — both in a tinted block at the bottom of
// the menu (nav-menu-refresh).
// No popup/sheet. After login/logout we tear down + re-open the WS so
// `bff/ws.py`'s session_middleware re-reads the cookie and the helper-side
// `thread_handler` resolves identity from `aveq_agent.profile` (or falls back to
// qbric's userProfile).
//
// nav-mobile-menu: this cluster now owns the page nav too, via `links`
// (an array of {label, href}). Desktop renders them as inline text links (when
// `desktopInlineLinks`) alongside your name; mobile collapses the links AND the
// name/"Log in" item into a hamburger (☰) sheet — the same pattern
// the dashboard and station headers share. mobile-nav-left: the hamburger sits
// at the LEFT edge of both headers and the sheet slides out from the left. The old "Home" link
// and the inline display-name are gone: the logo links home, and the account
// item carries identity.
//
// dashboard-revamp: the header is reused on /dashboard, which has no chat WS.
// `reconnectWs={false}` skips the login/logout WS bounce there (it would OPEN a
// stray chat socket, not refresh one). The dashboard shows its page nav as
// centre tabs on desktop, so it passes `desktopInlineLinks={false}` — the
// links appear only in the mobile hamburger here.
const navLinkStyle = {
  fontSize: '0.85rem', textDecoration: 'none', color: 'inherit',
  opacity: 0.8, whiteSpace: 'nowrap', background: 'none', border: 'none',
  cursor: 'pointer', fontFamily: 'inherit', padding: '4px 0',
};
// nav-item-hover: `background: undefined` DROPS navLinkStyle's inline
// `background: 'none'` — React omits undefined — so the `.auth-nav-item`
// class in AuthHeader.css owns the background and its :hover can take effect.
// (Inline beats class; with the inline `none` in place the hover never drew.)
// Menu tints, one steel-blue scale (lightest → strongest): the account section's background,
// hover (AuthHeader.css), the selected item. Every item, in or out of the account section, uses
// the same hover and selected tint.
const ACCOUNT_TINT = 'rgba(70, 130, 180, 0.06)';
const HOVER_TINT = 'rgba(70, 130, 180, 0.09)';   // must match .auth-nav-item:hover
const ITEM_SELECTED_TINT = 'rgba(70, 130, 180, 0.13)';

// The desktop header's inline links: the current page's link at full strength.
const inlineLinkStyle = (re) => (re.test(window.location.pathname)
  ? { ...navLinkStyle, opacity: 1, fontWeight: 600 } : navLinkStyle);

const sheetItemStyle = {
  ...navLinkStyle, background: undefined, opacity: 1, fontSize: '0.95rem', padding: '12px 14px',
  textAlign: 'left', display: 'block', width: '100%', borderRadius: '6px',
};
// nav-mobile-menu: highlight the sheet item matching the current route. No
// react-router in this app, so compare each link href to window.location.pathname.
const sheetItemActiveStyle = {
  ...sheetItemStyle, background: ITEM_SELECTED_TINT, fontWeight: 600,
};

// desktop-side-rail: the rail's items are the SHEET's items, at rest. Same
// vertical list, same order, same active highlight — it just never slides. The
// only difference is chrome: the rail is a permanent column in the page layout,
// so it has no backdrop, no portal, no Escape handler and no animation.
const railItemStyle = {
  ...sheetItemStyle, padding: '10px 12px',
};
const railItemActiveStyle = {
  ...railItemStyle, background: ITEM_SELECTED_TINT, fontWeight: 600,
};

// Width of the permanent desktop rail. Exported so the pages can RESERVE it:
// ChatInterface's containerStyle padding-left, and PageTopbar publishing
// `--rail-width` for `.directory`.
//
// It is NOT subtracted from anything. The output drawer is `position: fixed;
// right: 0` at `min(820px, 66vw)` — out of flow and anchored to the right edge,
// so it cannot be pushed off-centre and needs no adjustment (!641 review #3).
// The two would only overlap below a ~588px viewport, well under the desktop
// breakpoint.
export const RAIL_WIDTH = 200;
const SHEET_WIDTH = 260;

// Width left for the sheet's logo: the sheet (at most 80vw) less the nav's and the logo row's
// side padding.
const sheetLogoRoom = () => Math.min(SHEET_WIDTH, window.innerWidth * 0.8) - 20 - 28;

// Where logout takes you: the public homepage (signed-in visitors are sent on to /dashboard).
export const HOME_HREF = '/';

// Exported for the ordering guard in AuthHeader.test.js.
export const NAV_TINTS = { account: ACCOUNT_TINT, hover: HOVER_TINT, selected: ITEM_SELECTED_TINT };

// A link may declare `active` itself (PageTopbar passes its section key through,
// so nested routes light up); otherwise an exact path match.
const isLinkActive = (l) => (l.active !== undefined ? l.active : window.location.pathname === l.href);

// Separates the main menu from a page's own section (the station page's Network,
// Share link, Station settings).
const sectionDividerStyle = {
  height: '1px', background: 'rgba(0,0,0,0.1)', margin: '8px 12px', flexShrink: 0,
};

// nav-menu-refresh: the account block (your name + Log out, or Log in) sits at the
// bottom of the menu on a faint tint, apart from the page links above it.
const accountBlockStyle = {
  marginTop: 'auto', paddingTop: '6px', display: 'flex', flexDirection: 'column', gap: '2px',
};
const accountTintStyle = {
  display: 'flex', flexDirection: 'column', gap: 0, padding: '6px 4px',
  background: ACCOUNT_TINT, borderRadius: '10px',
};

export function useMessageUnread(signedIn, seq) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!signedIn) { setCount(0); return undefined; }
    let alive = true;
    const read = () => fetch('/api/v1/notifications?kind=inbox&unread=1&limit=1',
      { cache: 'no-store', credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => { if (alive && b) setCount(Number(b.message_unread_count) || 0); })
      .catch(() => {});
    read();
    // Once more shortly after: a live delivery (assist:deliver_mail) marks it read just after this.
    const again = setTimeout(read, 2000);
    const h = setInterval(read, 60000);
    return () => { alive = false; clearTimeout(again); clearInterval(h); };
  }, [signedIn, seq]);
  return count;
}

// Settings saves fire this so the menu follows without a reload.
export { SETTINGS_CHANGED };

// The person's settings (Settings page). Null until they load, so someone who turned the
// count off never sees it flash. A failed read gives {}: every setting at its default.
export function useAccountSettings(signedIn) {
  const [settings, setSettings] = useState(null);
  useEffect(() => {
    if (!signedIn) return undefined;
    let alive = true;
    const read = () => fetch('/auth/settings', { cache: 'no-store', credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => { if (alive) setSettings((b && b.settings) || {}); })
      .catch(() => { if (alive) setSettings({}); });
    read();
    window.addEventListener(SETTINGS_CHANGED, read);
    return () => { alive = false; window.removeEventListener(SETTINGS_CHANGED, read); };
  }, [signedIn]);
  return settings;
}

const AuthHeader = ({ links = [], desktopInlineLinks = true, reconnectWs = true,
                      onStationSettings = null,
                      showBrandLogo = false, variant = 'header' }) => {
  const dispatch = useDispatch();
  const isMobile = useIsMobile();
  // station-chat-web-push: needed on logout to drop this browser's push
  // subscription (the account id + the live station/session to send over).
  const chatStation = useSelector((s) => s.chat.station);
  const chatUser = useSelector((s) => s.chat.user);
  // Settings opened from a station's page shows that station's version under Software.
  const settingsHref = chatStation?.station?.station_id
    ? `/settings?station=${chatStation.station.station_id}` : '/settings';
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  // save-gallery-link: the sheet stays mounted briefly after close so its
  // slide-OUT animation can play (menuOpen = requested, menuRender = in DOM).
  const [menuRender, setMenuRender] = useState(false);
  // The account menu (Account / Settings / Log out) — open on those pages so you can see where you are.
  const [accountOpen, setAccountOpen] = useState(
    () => /^\/(account|profile|settings)/.test(window.location.pathname));
  // mailbox-menu: unread messages for the menu's Messages badge. Re-read when a message arrives
  // (the frame bumps scheduledRunSeq), when a chat opens (opening delivers, so it reads them), and
  // once a minute; nothing for a signed-out visitor.
  const runSeq = useSelector((s) => s.chat.scheduledRunSeq);
  const threadId = useSelector((s) => s.chat.threadId);
  const unread = useMessageUnread(Boolean(profile), `${runSeq}:${threadId || ''}`);
  const settings = useAccountSettings(Boolean(profile));
  const messageUnread = settings && settings.show_badge !== false ? unread : 0;
  // Still until a signed-in person's settings have loaded.
  const reduceMotion = loading || (Boolean(profile) && (!settings || settings.reduce_motion === true));
  // The rail's logo is always there, so it animates only when someone comes back to the tab.
  const returned = useReturnAfterAway(variant === 'rail');
  // nav-menu-refresh: open accordion groups, by key. One holding the current
  // page starts open so the active item is visible.
  const [openGroups, setOpenGroups] = useState(() => new Set(
    links.filter((l) => l.children && l.children.some(isLinkActive)).map((l) => l.key)));
  const toggleGroup = (key) => setOpenGroups((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  // station-share-link: a "Share" menu item on a station page copies the page's
  // /s/<url_token> address. null off a station page (dashboard / gallery) → no Share item.
  const [copied, setCopied] = useState(false);
  const _sharePath = getStationUrlPath();
  const shareUrl = _sharePath ? `${window.location.origin}${_sharePath}` : null;
  // station-install: Subscribe sits below Share link, on the same station pages.
  const subscription = useStationSubscription(shareUrl ? getPathStationId() : null, Boolean(profile));
  const subscribeLabel = subscription.subscribed ? 'Unsubscribe' : 'Subscribe';
  const copyShare = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      // Non-secure context (clipboard API unavailable, e.g. a plain-http LAN
      // dev host): fall back to a prompt so the link is still copyable by hand.
      window.prompt('Copy this station link:', shareUrl);
    }
  };

  // nav-brand-swap: the aveq logo now sits where the station avatar used to —
  // the trailing slot of the nav on desktop, and the TOP of the slide-out sheet
  // on mobile. The avatar moved to the head of the station header; it renders
  // from <StationPic> (extracted so the key/onError contract has one home).
  //
  // `showBrandLogo` is opt-in because the dashboard family (PageTopbar) already
  // draws a logo in its own top-left; without the flag those pages would carry
  // two. The SHEET logo is unconditional — the ask was for it on the dashboard
  // pages too, matching the station page.
  // The logo is DECORATION, not navigation — it carries no link. On the
  // dashboard family the AVE Q wordmark is the clickable brand anchor; on a
  // station page the nav links are the way out. That also drops it from the
  // confirm-before-leave path, so it can't strand a half-typed chat.
  // `width` is the room beside it; only a slot wide enough may get an animated logo.
  const brandLogo = (height, width = 0, cue, policy) => (
    <BrandLogo height={height} width={width} reduceMotion={reduceMotion} cue={cue} policy={policy} />
  );

  const refreshMe = async () => {
    try {
      const r = await fetch('/auth/me', { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setProfile(data.profile);
      } else {
        setProfile(null);
      }
    } catch (_) {
      setProfile(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refreshMe(); }, []);

  // Close the mobile hamburger sheet on Escape (its backdrop handles taps).
  useEffect(() => {
    if (!menuOpen) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setMenuOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [menuOpen]);

  // save-gallery-link: mount on open; delay unmount by the animation duration on
  // close so the slide-out plays. Keep in sync with AuthHeader.css (0.22s).
  useEffect(() => {
    if (menuOpen) { setMenuRender(true); return undefined; }
    if (!menuRender) return undefined;
    const t = setTimeout(() => setMenuRender(false), 220);
    return () => clearTimeout(t);
  }, [menuOpen, menuRender]);

  // gallery-title-menu-scroll: freeze the page behind the mobile nav slide-out so
  // the view underneath can't scroll while the menu is open. Keyed on menuOpen (not
  // menuRender) so the lock releases as the slide-out animation begins. guardTouch
  // swallows touch-scroll fall-through onto the backdrop; the sheet marks itself
  // [data-scroll-lock-allow] so its own content can still scroll.
  useBodyScrollLock(menuOpen && isMobile, { guardTouch: true });

  const reconnectWS = () => {
    if (!reconnectWs) return;
    // station-anon-login-prompt (!769 review #6): the bounce itself lives in
    // store/reconnectWs so the sign-in prompt's copy cannot drift from this one.
    // The `reconnectWs` PROP above stays here -- it is this header's opt-out, not
    // part of the bounce.
    sharedReconnectWs(dispatch);
  };

  const onLoggedIn = (newProfile) => {
    setProfile(newProfile);
    setModalOpen(false);
    reconnectWS();
  };

  const onLogout = async () => {
    if (!window.confirm('Log out of Ave Q?')) return;
    // station-chat-web-push: drop this browser's push subscription BEFORE we tear
    // down the session, so notifications stop on logout ("until logout"). Sent over
    // the still-live WS; best-effort (no-op if not in a room / no session).
    const accountId = chatUser && chatUser.user_id;
    const stationId = chatStation?.station?.station_id;
    const sessionId = chatStation?.sessionId;
    if (accountId && stationId && sessionId) {
      const send = (uri, params) => dispatch(sendGlobalAction(uri, params, sessionId));
      // Await so the endpoint-scoped unsubscribe frame is dispatched over the
      // still-live WS BEFORE we tear the session down below.
      await unsubscribeAccount(stationId, accountId, send);
    }
    try { await fetch('/auth/logout', { method: 'POST', credentials: 'include' }); } catch (_) {}
    setProfile(null);
    // Every page lands on the homepage after logout, as a full load, so nothing
    // signed-in (state, sockets, a chat an anonymous visitor can't drive) survives.
    window.location.href = HOME_HREF;
  };

  if (loading) return null;

  const profileName = profile && (profile.full_name || profile.display_name || profile.email);
  // Close the mobile sheet before running a nav action, so the page it leads to
  // isn't rendered behind an open sheet.
  const guardLeave = (run) => { setMenuOpen(false); run(); };

  // desktop-side-rail: the vertical item list, shared by the mobile slide-out
  // sheet and the permanent desktop rail. Extracted so the two cannot drift —
  // before this, adding a nav entry meant remembering to add it in two places,
  // and the desktop rail is the second place.
  //
  // `dismiss` is what a plain (unguarded) click does afterwards: the sheet closes
  // itself, the rail has nothing to close.
  // `itemRole` is 'menuitem' for the slide-out sheet and UNSET for the rail. The
  // sheet is a menu — it opens, traps attention, and closes on Escape. The rail
  // is permanent navigation, and `role="menuitem"` there would override the
  // implicit link/button roles to promise arrow-key menu semantics this doesn't
  // implement. Plain <a>/<button> inside <nav> is the honest markup, and it also
  // keeps the rail's items findable as links and buttons the way the horizontal
  // cluster's were.
  // nav-item-hover: every item carries `auth-nav-item` — the hover/focus
  // highlight lives in AuthHeader.css because inline styles have no :hover.
  // The ACTIVE item's background is still inline (activeStyle), which wins over
  // the class, so hovering it does not wash the highlight out.
  const verticalItems = ({ itemStyle, activeStyle, dismiss, itemRole }) => {
    const linkItem = (l, indent = false) => {
      const isActive = isLinkActive(l);
      const base = isActive ? activeStyle : itemStyle;
      return (
        <a key={l.href} href={l.href} role={itemRole} className="auth-nav-item"
           aria-current={isActive ? 'page' : undefined}
           style={indent ? { ...base, paddingLeft: '26px' } : base}
           onClick={dismiss}>
          <NavIcon name={l.icon} />{l.label}
          {l.badge === 'messages' && messageUnread > 0 && (
            <span className="nav-badge" aria-label={`${messageUnread} unread`}
                  style={{ marginLeft: 6, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
                           background: '#2563eb', color: '#fff', fontSize: 11, fontWeight: 700,
                           display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
              {messageUnread > 99 ? '99+' : messageUnread}
            </span>
          )}
        </a>
      );
    };
    // nav-menu-refresh: a link with `children` is an accordion — the header only
    // toggles; the children are the destinations.
    const group = (g) => {
      const open = openGroups.has(g.key);
      const panelId = `nav-group-${g.key}`;
      return (
        <React.Fragment key={g.key}>
          <button type="button" role={itemRole} className="auth-nav-item"
                  aria-expanded={open} aria-controls={panelId}
                  onClick={() => toggleGroup(g.key)}
                  style={{ ...itemStyle, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <span><NavIcon name={g.icon} />{g.label}</span>
            <svg aria-hidden="true" width="12" height="12" viewBox="0 0 16 16" fill="none"
                 stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                 style={{ opacity: 0.6, transition: 'transform 0.15s ease',
                          transform: open ? 'rotate(180deg)' : 'none' }}>
              <path d="M4 6l4 4 4-4" />
            </svg>
          </button>
          {open && (
            <div id={panelId} style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
              {g.children.map((c) => linkItem(c, true))}
            </div>
          )}
        </React.Fragment>
      );
    };
    // Your name and Log out read as one block: a full item's vertical padding on
    // each left a menu-sized gap between them. One `padding` value, not longhands
    // over the shorthand, so a re-render can't drop either.
    const inSheet = itemRole === 'menuitem';
    const accountItemStyle = { ...itemStyle, padding: inSheet ? '5px 14px' : '5px 12px' };
    // On a phone the sign-in/out control keeps a 44px tap area; the text stays at
    // the top so it still sits right under your name.
    const accountActionStyle = inSheet
      ? { ...accountItemStyle, minHeight: '44px', display: 'flex', alignItems: 'flex-start' }
      : accountItemStyle;
    // Account and Settings show which one you're on, like the page links above.
    const accountLinkStyle = (current) => ({
      ...accountItemStyle, paddingLeft: '26px',
      ...(current ? { background: ITEM_SELECTED_TINT, fontWeight: 600 } : {}),
    });
    const onAccount = /^\/(account|profile)/.test(window.location.pathname);
    const onSettings = /^\/settings/.test(window.location.pathname);
    return (
      <>
        {links.map((l) => {
          if (l.divider) return <div key={l.key} role="separator" style={sectionDividerStyle} />;
          return l.children ? group(l) : linkItem(l);
        })}
        {shareUrl && (
          <button type="button" role={itemRole} className="auth-nav-item" onClick={copyShare} style={itemStyle}
                  title="Copy this station's link">
            <NavIcon name="share" />{copied ? 'Copied!' : 'Share link'}
          </button>
        )}
        {subscription.subscribed !== null && (
          <button type="button" role={itemRole} className="auth-nav-item" style={itemStyle}
                  onClick={subscription.toggle} disabled={subscription.busy}
                  aria-pressed={subscription.subscribed}
                  title="Show this station on your Stations page">
            <NavIcon name={subscription.subscribed ? 'subscribed' : 'subscribe'} />{subscribeLabel}
          </button>
        )}
        {subscription.error && (
          <div role="status" style={{ padding: '0 14px 6px 38px', fontSize: '0.78rem', color: '#b3261e' }}>
            {subscription.error}
          </div>
        )}
        {/* station-user-settings: shown only when the station has settings to set. */}
        {onStationSettings && (
          <button type="button" role={itemRole} className="auth-nav-item" style={itemStyle}
                  onClick={() => { dismiss(); onStationSettings(); }}>
            <NavIcon name="settings" />Station settings
          </button>
        )}
        <div style={accountBlockStyle}>
          <div style={accountTintStyle}>
            {profile ? (
              <>
                {/* The account is just your name; it opens Account (personal information,
                    sign-in), Settings (global settings) and Log out. */}
                <button type="button" role={itemRole} className="auth-nav-item" style={accountItemStyle}
                        aria-expanded={accountOpen} onClick={() => setAccountOpen((o) => !o)}>
                  <NavIcon name="user" /><strong>{profileName}</strong>
                </button>
                {accountOpen && (
                  <>
                    <a href="/account" role={itemRole} className="auth-nav-item" onClick={dismiss}
                       aria-current={onAccount ? 'page' : undefined} style={accountLinkStyle(onAccount)}>
                      Account
                    </a>
                    <a href={settingsHref} role={itemRole} className="auth-nav-item" onClick={dismiss}
                       aria-current={onSettings ? 'page' : undefined} style={accountLinkStyle(onSettings)}>
                      Settings
                    </a>
                    <button type="button" role={itemRole} className="auth-nav-item" onClick={() => guardLeave(onLogout)}
                            style={{ ...accountActionStyle, paddingLeft: '26px' }}>
                      Log out
                    </button>
                  </>
                )}
              </>
            ) : (
              <button type="button" role={itemRole} className="auth-nav-item" onClick={() => guardLeave(() => setModalOpen(true))} style={accountActionStyle}>
                <NavIcon name="login" />Log in
              </button>
            )}
          </div>
        </div>
      </>
    );
  };

  // desktop-side-rail: the permanent left column. The PAGE places it (it is the
  // first child of a flex row wrapping the whole shell), which is why this
  // returns a bare <nav> and not a portal — unlike the mobile sheet, it is part
  // of the layout rather than floating above it, so the main panel reflows
  // beside it instead of being covered.
  if (variant === 'rail') {
    return (
      <>
        <nav aria-label="Main"
             style={{
               // Pinned to the viewport's left edge for its full height. The host
               // page reserves the width with padding-left (see ChatInterface's
               // containerStyle), so content flows beside it rather than under it.
               position: 'fixed', left: 0, top: 0, bottom: 0,
               width: `${RAIL_WIDTH}px`,
               // Above page content, and below the modals / confirm dialogs
               // (3000) so a dialog raised from the rail still stacks over it.
               //
               // It is ALSO above the output drawer (`drawerStyle` is 40, its
               // reopen button 41) — the opposite of what this comment first
               // claimed (!641 review #4). That is safe only because the two
               // never share pixels: the drawer is right-anchored at
               // min(820px, 66vw) and the rail owns the leftmost 200px, which
               // can only meet below a ~588px viewport. If the drawer is ever
               // made full-width on DESKTOP (it already is on mobile, where
               // there is no rail), the rail would sit on top of it and this
               // number has to come down.
               zIndex: 900,
               display: 'flex', flexDirection: 'column',
               padding: '14px 10px', gap: '2px',
               borderRight: '1px solid rgba(0,0,0,0.08)',
               background: 'rgba(255,255,255,0.55)',
               backdropFilter: 'blur(16px) saturate(1.4)',
               WebkitBackdropFilter: 'blur(16px) saturate(1.4)',
               overflowY: 'auto',
             }}>
          {/* Brand at the top of the rail, matching the sheet. Decoration, not
              navigation — same as everywhere else, so it can't strand a
              half-typed chat via the confirm-before-leave path. */}
          <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 14px 16px' }}>
            {brandLogo(44, RAIL_WIDTH - 20 - 28, returned, EVERY_TIME)}
          </div>
          {verticalItems({
            itemStyle: railItemStyle,
            activeStyle: railItemActiveStyle,
            dismiss: () => {},
            itemRole: undefined,   // permanent nav, not a menu — see verticalItems
          })}
        </nav>
        {/* One of TWO LoginModal render sites — the other is at the end of the
            header branch below. Only one ever mounts (this branch returns
            early), but they must be kept in step (!641 review). */}
        {modalOpen && (
          <LoginModal onClose={() => setModalOpen(false)} onSuccess={onLoggedIn} />
        )}
      </>
    );
  }

  return (
    <>
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'flex-end',
        // Desktop spaces the links + profile item with a comfortable gap; on
        // mobile the bell + hamburger read as a tight pair.
        gap: isMobile ? '2px' : '16px',
        position: 'relative',
      }}>
        {!isMobile ? (
          // Desktop: page links (when this header shows them inline) + the
          // profile / login item, all as plain text.
          <>
            {desktopInlineLinks && links.filter((l) => !l.divider).map((l) => (
              <a key={l.href} href={l.href} style={navLinkStyle}>
                {l.label}
              </a>
            ))}
            {shareUrl && (
              <button type="button" onClick={copyShare} style={navLinkStyle}
                      title="Copy this station's link">
                {copied ? 'Copied!' : 'Share'}
              </button>
            )}
            {subscription.subscribed !== null && (
              <button type="button" onClick={subscription.toggle} disabled={subscription.busy}
                      aria-pressed={subscription.subscribed} style={navLinkStyle}
                      title={subscription.error || 'Show this station on your Stations page'}>
                {subscribeLabel}
              </button>
            )}
            {onStationSettings && (
              <button type="button" onClick={onStationSettings} style={navLinkStyle}>
                Station settings
              </button>
            )}
            {profile ? (
              // The inline cluster has room for all three: name (→ Account), Settings, Log out.
              <>
                <a href="/account" title="Your account" style={inlineLinkStyle(/^\/(account|profile)/)}
                   aria-current={/^\/(account|profile)/.test(window.location.pathname) ? 'page' : undefined}>
                  {profileName}
                </a>
                <a href={settingsHref} style={inlineLinkStyle(/^\/settings/)}
                   aria-current={/^\/settings/.test(window.location.pathname) ? 'page' : undefined}>Settings</a>
                <button type="button" onClick={() => guardLeave(onLogout)} style={navLinkStyle}>
                  Log out
                </button>
              </>
            ) : (
              <button type="button" onClick={() => guardLeave(() => setModalOpen(true))} style={navLinkStyle}>
                Log in
              </button>
            )}
            {/* nav-brand-swap: … | User | Log out | aveq logo (rightmost).
                Was the station avatar; the avatar now leads the header. */}
            {showBrandLogo && brandLogo(34)}
          </>
        ) : (
          // Mobile: the hamburger opens a slide-out sheet from the LEFT
          // (mobile-nav-left: it was the right; the station header now puts
          // the hamburger at its left edge and the station avatar at its
          // right, so the sheet comes out from the side its trigger is on),
          // with a dimmed backdrop — not a dropdown.
          <>
            <button
              type="button"
              onClick={() => setMenuOpen((o) => !o)}
              aria-label="Menu"
              aria-haspopup="menu"
              aria-expanded={menuOpen}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px 6px', fontSize: '1.4rem', lineHeight: 1,
                display: 'flex', alignItems: 'center', color: 'inherit',
              }}>
              ☰
            </button>
            {menuRender && createPortal((
              // mobile-sheet-portal: portal to <body> so the sheet escapes the
              // top-panel's stacking context (sticky + zIndex on mobile) and sits
              // above station-page siblings like the output drawer — same reason
              // the Leave-station dialog is portaled. zIndex below that dialog (3000)
              // so a confirm raised FROM the sheet still stacks on top.
              <>
                <div
                  className={menuOpen ? 'auth-backdrop-in' : 'auth-backdrop-out'}
                  onClick={() => setMenuOpen(false)}
                  // gallery-title-menu-scroll: touchAction none + overscroll contain so a
                  // touch-drag on the dim backdrop can't scroll the page behind it.
                  style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.28)', zIndex: 2000,
                           touchAction: 'none', overscrollBehavior: 'contain' }} />
                <div role="menu"
                  // gallery-title-menu-scroll: the sheet opts its own content back IN to
                  // scrolling (the guardTouch lock swallows touchmove everywhere else).
                  data-scroll-lock-allow
                  className={menuOpen ? 'auth-sheet-in' : 'auth-sheet-out'}
                  style={{
                  // mobile-slideout-polish: translucent sheet flush to the top/bottom and
                  // one side edge — only the INNER corners are rounded. The × is gone; the
                  // sheet is narrower than the screen so the dimmed backdrop shows beside it
                  // and stays tappable to close.
                  // mobile-nav-left: LEFT-anchored (was right) — inner corners are now the
                  // right ones, the shadow falls rightward, and AuthHeader.css slides it in
                  // from -100%.
                  position: 'fixed', top: 0, left: 0, bottom: 0, width: `${SHEET_WIDTH}px`, maxWidth: '80vw',
                  background: 'rgba(255,255,255,0.7)',
                  backdropFilter: 'blur(16px) saturate(1.4)', WebkitBackdropFilter: 'blur(16px) saturate(1.4)',
                  borderTopRightRadius: 18, borderBottomRightRadius: 18,
                  zIndex: 2001, boxShadow: '8px 0 34px rgba(0,0,0,0.24)',
                  display: 'flex', flexDirection: 'column', overflow: 'hidden',
                }}>
                  <nav style={{ display: 'flex', flexDirection: 'column', padding: '14px 10px 12px',
                                flex: 1, minHeight: 0, overflowY: 'auto', gap: '2px' }}>
                    {/* nav-brand-swap: the aveq logo, centered at the TOP of the sheet
                        above the nav items — on the station page AND the dashboard
                        family (Stations / Networks / Gallery / Scheduled Tasks), which
                        previously had nothing here. Replaces the station avatar, which
                        moved to the head of the station header. */}
                    <div style={{ display: 'flex', justifyContent: 'center', padding: '6px 14px 14px' }}>
                      {brandLogo(44, sheetLogoRoom())}
                    </div>
                    {/* desktop-side-rail: shared with the permanent rail — one
                        definition, so a new nav entry can't land in one and miss
                        the other. */}
                    {verticalItems({
                      itemStyle: sheetItemStyle,
                      activeStyle: sheetItemActiveStyle,
                      dismiss: () => setMenuOpen(false),
                      itemRole: 'menuitem',
                    })}
                  </nav>
                </div>
              </>
            ), document.body)}
          </>
        )}

      </div>
      {/* The other LoginModal site; the rail branch above has its own. Only one
          branch mounts at a time — keep the two in step. */}
      {modalOpen && (
        <LoginModal
          onClose={() => setModalOpen(false)}
          onSuccess={onLoggedIn} />
      )}
    </>
  );
};

export default AuthHeader;
